import { useState, useMemo, useEffect } from "react";
import { MultiScaleLongitudinalChart } from "./MultiScaleLongitudinalChart";
import { RawDataDisplay } from "./RawDataDisplay";
import { DateRange } from "./DateRangeFilter";
import { CriteriaTable } from "./CriteriaTable";
import { AssessmentSelector } from "./AssessmentSelector";
import {
  AssessmentSummary,
  QuestionResponse,
  MultiScaleDataPointWithId,
} from "./AssessmentSummary";
import { ClinicalMeasurement } from "../../../../types";
import { filterByDateRange } from "../../../../utils/filterByDateRange";
import { computeInitialBaseline } from "../../../../utils/baselineUtils";
export interface DataPoint {
  id: number;
  date: string;
  score: number;
  interpretation: string;
  responder: string; // Add responder
}

interface MeasureDetailViewProps {
  measureName: string;
  longitudinalData?: DataPoint[];
  rawData: QuestionResponse[];
  globalDateRange: DateRange;
  measureDataMap: Record<string, ClinicalMeasurement>;
}

export function MeasureDetailView({
  measureName,
  longitudinalData,
  rawData,
  globalDateRange,
  measureDataMap,
}: MeasureDetailViewProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(true);
  const [isRawDataExpanded, setIsRawDataExpanded] = useState(true);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);

  const measureData = measureDataMap[measureName];
  const criteria = measureData?.criteria;

  // Unified Selection Logic
  const handlePointClick = (uniqueId: number) => {
    setSelectedIds((prev) => {
      // Rule 1: Prevent deselecting the only selection
      if (prev.length === 1 && prev[0] === uniqueId) {
        return prev;
      }

      // Rule 2: If it's already selected and there are 2, remove it
      if (prev.includes(uniqueId)) {
        return prev.filter((id) => id !== uniqueId);
      }

      // Rule 3: If we have 2 selected and click a 3rd, replace the oldest selection
      if (prev.length >= 2) {
        // Keep the second item (most recently selected) and add the new one
        return [prev[1], uniqueId];
      }

      // Rule 4: Standard append
      return [...prev, uniqueId];
    });
  };

  const handlePrimarySelect = (id: number) => {
    setSelectedIds((prev) => {
      // If the user selects the same ID as comparison, drop comparison
      if (prev.length === 2 && prev[1] === id) {
        return [id];
      }
      if (prev.length === 2) {
        return [id, prev[1]];
      }
      return [id];
    });
  };

  const handleComparisonSelect = (id: number | null) => {
    setSelectedIds((prev) => {
      if (id === null || id === prev[0]) return [prev[0]];
      return [prev[0], id];
    });
  };

  // Determine initial baseline data if this is a reassessment (Vanderbilt)
  // or a repeated screener (PHQ-9, GAD-7). Logic is in baselineUtils.ts.
  const initialBaseline = useMemo(
    () => computeInitialBaseline(measureName, measureDataMap),
    [measureName, measureDataMap],
  );

  // Format all data as multi-scale
  const formattedLongitudinalData = useMemo(() => {
    if (!longitudinalData) return [];

    return longitudinalData.map((d) => {
      // If it already has subscales, it's multiscale
      if (
        "subscales" in d &&
        Array.isArray((d as Record<string, unknown>).subscales)
      ) {
        return d as unknown as MultiScaleDataPointWithId;
      }

      // Map single scale to multiscale format
      return {
        id: d.id,
        date: d.date,
        responder: d.responder,
        subscales: [
          {
            name: measureName,
            score: d.score,
            interpretation: d.interpretation,
          },
        ],
      } as MultiScaleDataPointWithId;
    });
  }, [longitudinalData, measureName]);

  // Filter multi-scale data
  const filteredMultiScaleData = useMemo(() => {
    return filterByDateRange(formattedLongitudinalData, globalDateRange);
  }, [formattedLongitudinalData, globalDateRange]);

  // Also filter the Raw Data list to match the chart range
  const filteredRawData = useMemo(() => {
    return filterByDateRange(rawData, globalDateRange);
  }, [rawData, globalDateRange]);

  // Sync selections when data filters change
  useEffect(() => {
    if (filteredRawData.length === 0) {
      setTimeout(() => {
        if (selectedIds.length > 0) setSelectedIds([]);
      }, 0);
      return;
    }

    const validIds = new Set(filteredRawData.map((d) => d.id));
    const filteredSelection = selectedIds.filter((id) => validIds.has(id));

    // If selection is now empty (because all were filtered out) or changed
    if (filteredSelection.length === 0) {
      // Reset to most recent available
      setTimeout(
        () => setSelectedIds([filteredRawData[filteredRawData.length - 1].id]),
        0,
      );
    } else if (filteredSelection.length !== selectedIds.length) {
      // Keep what's still valid
      setTimeout(() => setSelectedIds(filteredSelection), 0);
    }
  }, [filteredRawData, selectedIds]); // We only care when the filtered list changes

  const handleClearFilter = () => {
    // If we have data, we should auto-select the latest instead of true clearing
    let latestId: number | undefined;
    if (filteredMultiScaleData.length > 0) {
      latestId = filteredMultiScaleData[filteredMultiScaleData.length - 1].id;
    }
    if (latestId !== undefined) {
      setSelectedIds([latestId]);
    } else {
      setSelectedIds([]);
    }
  };

  // Prepare criteria data with responders
  const enrichedCriteria = useMemo(() => {
    if (!criteria) return undefined;
    return criteria.map((c) => {
      const rawEntry = rawData.find((r) => r.id === c.id);
      return {
        ...c,
        responder: rawEntry?.responder || "Unknown",
      };
    });
  }, [criteria, rawData]);

  const isVanderbilt = measureName.toLowerCase().includes("vanderbilt");

  return (
    <div
      className="p-6 h-full flex flex-col gap-6"
      style={{ backgroundColor: "#F5F7FA" }}
    >
      {/* Assessment Selection Dropdowns */}
      <AssessmentSelector
        assessments={filteredRawData}
        selectedIds={selectedIds}
        onPrimarySelect={handlePrimarySelect}
        onComparisonSelect={handleComparisonSelect}
        onReset={handleClearFilter}
      />

      {/* 1. Assessment Summary Section (Top - for Vanderbilt only) */}
      {isVanderbilt && (
        <div
          className="bg-white rounded-lg border shadow-sm"
          style={{ borderColor: "#E2E8F0" }}
        >
          <button
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
              Assessment Summary
            </h3>
            <svg
              className={`w-5 h-5 transition-transform ${isSummaryExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: "#64748B" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isSummaryExpanded && (
            <div
              className="p-4 pt-0 border-t"
              style={{ borderColor: "#E2E8F0" }}
            >
              <AssessmentSummary
                selectedData={filteredRawData
                  .filter((d) => selectedIds.includes(d.id))
                  .sort(
                    (a, b) =>
                      selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id),
                  )}
                longitudinalData={filteredMultiScaleData}
                criteriaData={enrichedCriteria}
              />
            </div>
          )}
        </div>
      )}

      {/* 2. Criteria Section */}
      {criteria && criteria.length > 0 && (
        <div
          className="bg-white rounded-lg border shadow-sm"
          style={{ borderColor: "#E2E8F0" }}
        >
          <button
            onClick={() => setIsCriteriaExpanded(!isCriteriaExpanded)}
            className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
              Criteria
            </h3>
            <svg
              className={`w-5 h-5 transition-transform ${isCriteriaExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: "#64748B" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isCriteriaExpanded && (
            <div
              className="p-4 pt-0 border-t"
              style={{ borderColor: "#E2E8F0" }}
            >
              <CriteriaTable
                criteria={enrichedCriteria || []}
                selectedIds={selectedIds}
              />
            </div>
          )}
        </div>
      )}

      {/* 3. Score History Section (shown before Raw Data for PHQ/GAD; after for Vanderbilt) */}
      {!isVanderbilt && (
        <div
          className="bg-white rounded-lg border shadow-sm"
          style={{ borderColor: "#E2E8F0" }}
        >
          <button
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
                Score History
              </h3>
              {selectedIds.length === 2 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-tight">
                  Comparison Mode
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {globalDateRange.label !== "All History" && (
                <span className="text-xs font-medium px-2 py-1 bg-blue-50 text-blue-600 rounded">
                  Filtered: {globalDateRange.label}
                </span>
              )}
              <svg
                className={`w-5 h-5 transition-transform ${isHistoryExpanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: "#64748B" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {isHistoryExpanded && (
            <div
              className="p-4 pt-0 border-t"
              style={{ borderColor: "#E2E8F0" }}
            >
              <MultiScaleLongitudinalChart
                data={filteredMultiScaleData}
                measureName={measureName}
                onPointClick={handlePointClick}
                selectedIds={selectedIds}
                initialBaseline={initialBaseline}
              />

              {selectedIds.length === 0 ? (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  Click data points to filter or compare assessments.
                </p>
              ) : selectedIds.length === 1 ? (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  Select a second data point to enable side-by-side comparison.
                </p>
              ) : (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  Comparing selected assessments. Differences are highlighted
                  below.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 4. Raw Data Section */}
      <div
        className="bg-white rounded-lg border shadow-sm"
        style={{ borderColor: "#E2E8F0" }}
      >
        <button
          onClick={() => setIsRawDataExpanded(!isRawDataExpanded)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
        >
          <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
            Raw Data
          </h3>
          <svg
            className={`w-5 h-5 transition-transform ${isRawDataExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: "#64748B" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isRawDataExpanded && (
          <div className="p-4 pt-0 border-t" style={{ borderColor: "#E2E8F0" }}>
            <RawDataDisplay
              data={filteredRawData}
              measureName={measureName}
              selectedIds={selectedIds}
              onClearSelection={handleClearFilter}
            />
          </div>
        )}
      </div>

      {/* 5. Score History Section (Vanderbilt only — kept after Raw Data per clinician preference) */}
      {isVanderbilt && (
        <div
          className="bg-white rounded-lg border shadow-sm"
          style={{ borderColor: "#E2E8F0" }}
        >
          <button
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
                Score History
              </h3>
              {selectedIds.length === 2 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-tight">
                  Comparison Mode
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Show a badge if filtered */}
              {globalDateRange.label !== "All History" && (
                <span className="text-xs font-medium px-2 py-1 bg-blue-50 text-blue-600 rounded">
                  Filtered: {globalDateRange.label}
                </span>
              )}
              <svg
                className={`w-5 h-5 transition-transform ${isHistoryExpanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: "#64748B" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {isHistoryExpanded && (
            <div
              className="p-4 pt-0 border-t"
              style={{ borderColor: "#E2E8F0" }}
            >
              <MultiScaleLongitudinalChart
                data={filteredMultiScaleData}
                measureName={measureName}
                onPointClick={handlePointClick}
                selectedIds={selectedIds}
                initialBaseline={initialBaseline}
              />

              {selectedIds.length === 0 ? (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  Click data points to filter or compare assessments.
                </p>
              ) : selectedIds.length === 1 ? (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  Select a second data point to enable side-by-side comparison.
                </p>
              ) : (
                <p className="text-xs mt-3 italic" style={{ color: "#64748B" }}>
                  {/* Note: This logic assumes you can derive dates from IDs or have access to the original data */}
                  Comparing selected assessments. Differences are highlighted
                  below.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
